import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shell } from '../components/Shell';
import { getActiveTenant, setBrandingForTenant } from '../lib/tenant';

export function OnboardingBrandingPage() {
  const navigate = useNavigate();
  const tenantId = getActiveTenant();
  const [theme, setTheme] = useState<'auto' | 'light' | 'dark'>('auto');
  const [accent, setAccent] = useState('#7B3FE4');
  const [saving, setSaving] = useState(false);

  const finish = async (skip: boolean) => {
    if (saving) return;
    setSaving(true);
    if (!skip && tenantId) {
      try {
        await setBrandingForTenant(tenantId, { theme, accentColor: accent });
      } catch (err) {
        console.warn('branding save failed', err);
      }
    }
    navigate('/install');
  };

  if (!tenantId) {
    return (
      <Shell centerLabel="Onboarding · 2 of 2">
        <section className="border-b" style={{ borderColor: 'var(--rule)' }}>
          <div className="max-w-[640px] mx-auto px-6 md:px-14 py-20">
            <div className="meta mb-3" style={{ color: 'var(--flag)' }}>No workspace</div>
            <p className="text-[14px] leading-[1.55]" style={{ color: 'rgba(13,11,31,0.75)' }}>
              You haven&apos;t completed step 1. <a href="/onboarding/site" className="underline" style={{ color: 'var(--ink)' }}>Start there.</a>
            </p>
          </div>
        </section>
      </Shell>
    );
  }

  return (
    <Shell centerLabel="Onboarding · 2 of 2">
      <section className="border-b" style={{ borderColor: 'var(--rule)' }}>
        <div className="max-w-[1100px] mx-auto px-6 md:px-14 py-14 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-14 items-start">
          <div>
            <span className="pill mb-3 inline-flex">
              <span className="pill-dot" /> 04 · Branding · optional · skip if you want
            </span>
            <h1 className="font-medium mt-4 mb-3" style={{ fontSize: 'clamp(28px, 3.4vw, 44px)', lineHeight: 1.04, letterSpacing: '-0.03em' }}>
              Make it look like <span className="grad-text">your site</span>.
            </h1>
            <p className="text-[15px] leading-[1.55] mb-10 max-w-[560px]" style={{ color: 'rgba(13,11,31,0.7)' }}>
              Optional. Defaults are fine. Tweak colors, choose a theme. Banner copy and legal text are managed separately so you don&apos;t accidentally break them.
            </p>

            <div style={{ borderTop: '1px solid var(--rule-soft)' }}>
              <Row label="Theme" hint="Auto follows the user's OS setting.">
                <select value={theme} onChange={(e) => setTheme(e.target.value as 'auto' | 'light' | 'dark')} style={{ padding: '8px 12px', fontSize: 13, border: '1px solid var(--rule)', borderRadius: 3, background: 'white' }}>
                  <option value="auto">Auto (light/dark)</option>
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                </select>
              </Row>
              <Row label="Brand accent" hint="Used for the primary button.">
                <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} style={{ width: 50, height: 30, padding: 0, border: '1px solid var(--rule)', cursor: 'pointer' }} />
              </Row>
              <Row label="Languages" hint="Auto-detected from your site. Override later in the dashboard.">
                <span className="text-xs" style={{ color: 'var(--muted-ink)' }}>EN auto</span>
              </Row>
              <Row
                label={
                  <>
                    Hide &ldquo;Powered by WeDPO&rdquo;{' '}
                    <span
                      style={{
                        marginLeft: 8,
                        padding: '2px 8px',
                        background: 'var(--grad)',
                        color: 'white',
                        borderRadius: 99,
                        fontFamily: 'IBM Plex Mono, monospace',
                        fontSize: 9,
                        letterSpacing: '1.2px',
                        textTransform: 'uppercase',
                        fontWeight: 500,
                        verticalAlign: 'middle',
                      }}
                    >
                      Pro only
                    </span>
                  </>
                }
                hint="Not available on the Free plan. Upgrade to Solo or Agency to remove the attribution."
                locked
              >
                <ToggleSwitch checked={false} disabled />
              </Row>
            </div>

            <div className="flex gap-3 mt-8">
              <button className="btn" onClick={() => finish(false)} disabled={saving}>
                <span>{saving ? 'Saving…' : 'Finish setup'}</span>
                {!saving && <span className="slash">/</span>}
              </button>
              <button className="btn btn-ghost" onClick={() => finish(true)} disabled={saving}>
                Skip for now
              </button>
            </div>
          </div>

          <div>
            <span className="meta block mb-3">Live preview</span>
            <BrandingPreview accent={accent} theme={theme} />
          </div>
        </div>
      </section>
    </Shell>
  );
}

function Row({
  label,
  hint,
  children,
  locked,
}: {
  label: React.ReactNode;
  hint: string;
  children: React.ReactNode;
  locked?: boolean;
}) {
  return (
    <div
      className="flex items-center justify-between py-3"
      style={{ borderBottom: '1px solid var(--rule-soft)', opacity: locked ? 0.75 : 1 }}
    >
      <div>
        <div style={{ fontSize: 13.5 }}>{label}</div>
        <span style={{ color: 'var(--muted-ink)', fontSize: 11.5, marginTop: 2, display: 'block' }}>{hint}</span>
      </div>
      {children}
    </div>
  );
}

function ToggleSwitch({ checked, disabled }: { checked: boolean; disabled?: boolean }) {
  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-block',
        width: 38,
        height: 22,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        style={{
          position: 'absolute',
          inset: 0,
          background: checked ? 'var(--ink)' : 'var(--paper-2)',
          borderRadius: 99,
          border: '1px solid var(--rule)',
        }}
      />
      <span
        style={{
          position: 'absolute',
          width: 16,
          height: 16,
          top: 2,
          left: 2,
          background: 'white',
          borderRadius: '50%',
          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          transform: checked ? 'translateX(16px)' : 'translateX(0)',
          transition: 'transform 0.2s',
        }}
      />
    </span>
  );
}

function BrandingPreview({ accent, theme }: { accent: string; theme: 'auto' | 'light' | 'dark' }) {
  const dark = theme === 'dark';
  return (
    <div
      style={{
        background: 'white',
        border: '1px solid var(--rule)',
        borderRadius: 6,
        padding: 18,
        minHeight: 320,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          background: 'linear-gradient(180deg, #fafaf6 0%, #ede9e1 100%)',
          height: '100%',
          minHeight: 300,
          borderRadius: 4,
          position: 'relative',
          padding: 24,
        }}
      >
        <div style={{ height: 8, background: 'rgba(13,11,31,0.08)', borderRadius: 4, marginBottom: 10 }} />
        <div style={{ height: 8, width: '60%', background: 'rgba(13,11,31,0.08)', borderRadius: 4, marginBottom: 10 }} />
        <div style={{ height: 8, background: 'rgba(13,11,31,0.08)', borderRadius: 4, marginBottom: 10 }} />
        <div
          style={{
            position: 'absolute',
            left: 16,
            right: 16,
            bottom: 16,
            background: dark ? 'var(--ink)' : 'var(--paper)',
            color: dark ? 'var(--paper)' : 'var(--ink)',
            border: '1px solid var(--rule)',
            borderRadius: 6,
            padding: '14px 16px',
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: 14,
            alignItems: 'center',
            boxShadow: '0 14px 30px -15px rgba(13,11,31,0.25)',
          }}
        >
          <div>
            <h4 style={{ margin: 0, fontSize: 13.5, fontWeight: 500 }}>We use essential cookies, and ask before anything else</h4>
            <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.45, color: dark ? 'rgba(240,238,233,0.7)' : 'rgba(13,11,31,0.7)' }}>
              yourdomain.com uses a small set of necessary technologies. See our cookie policy.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={{
                padding: '6px 12px',
                fontSize: 11.5,
                borderRadius: 3,
                cursor: 'pointer',
                border: `1px solid ${dark ? 'var(--paper)' : 'var(--ink)'}`,
                background: 'transparent',
                color: dark ? 'var(--paper)' : 'var(--ink)',
              }}
            >
              Reject
            </button>
            <button
              style={{
                padding: '6px 12px',
                fontSize: 11.5,
                borderRadius: 3,
                cursor: 'pointer',
                border: 'none',
                background: accent,
                color: 'white',
              }}
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
